import { useState } from 'react';
import { X, AlertTriangle, File, FileText, Code } from 'lucide-react';
import type { FileType } from '@/routes/chat/hooks/use-chat';

interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NewFileDialogProps extends BaseDialogProps {
  parentPath?: string;
  onConfirm: (fileName: string, parentPath?: string) => void;
}

interface NewFolderDialogProps extends BaseDialogProps {
  parentPath?: string;
  onConfirm: (folderName: string, parentPath?: string) => void;
}

interface RenameDialogProps extends BaseDialogProps {
  file: FileType;
  onConfirm: (file: FileType, newName: string) => void;
}

interface DeleteDialogProps extends BaseDialogProps {
  file: FileType;
  onConfirm: (file: FileType) => void;
}

interface PropertiesDialogProps extends BaseDialogProps {
  file: FileType;
}

export function NewFileDialog({ isOpen, onClose, parentPath, onConfirm }: NewFileDialogProps) {
  const [fileName, setFileName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fileName.trim()) {
      onConfirm(fileName.trim(), parentPath);
      setFileName('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">New File</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="size-4" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">File Name</label>
            {parentPath && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Location: {parentPath}/
              </p>
            )}
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Enter file name (e.g., component.tsx)"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg"
              autoFocus
            />
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!fileName.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function NewFolderDialog({ isOpen, onClose, parentPath, onConfirm }: NewFolderDialogProps) {
  const [folderName, setFolderName] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (folderName.trim()) {
      onConfirm(folderName.trim(), parentPath);
      setFolderName('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">New Folder</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="size-4" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Folder Name</label>
            {parentPath && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Location: {parentPath}/
              </p>
            )}
            <input
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name"
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg"
              autoFocus
            />
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!folderName.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg disabled:opacity-50"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function RenameDialog({ isOpen, onClose, file, onConfirm }: RenameDialogProps) {
  const [newName, setNewName] = useState(file?.filePath.split('/').pop() || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim() && file) {
      onConfirm(file, newName.trim());
      onClose();
    }
  };

  if (!isOpen || !file) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Rename</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="size-4" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">New Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg"
              autoFocus
            />
          </div>
          
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!newName.trim()}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg disabled:opacity-50"
            >
              Rename
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function DeleteDialog({ isOpen, onClose, file, onConfirm }: DeleteDialogProps) {
  const handleConfirm = () => {
    if (file) {
      onConfirm(file);
      onClose();
    }
  };

  if (!isOpen || !file) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="size-6 text-red-500" />
          <h3 className="text-lg font-medium">Delete File</h3>
        </div>
        
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Are you sure you want to delete <strong>{file.filePath}</strong>? This action cannot be undone.
        </p>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export function PropertiesDialog({ isOpen, onClose, file }: PropertiesDialogProps) {
  if (!isOpen || !file) return null;

  const getFileIcon = (filePath: string) => {
    const ext = filePath.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
        return Code;
      case 'md':
      case 'txt':
        return FileText;
      default:
        return File;
    }
  };

  const FileIcon = getFileIcon(file.filePath);
  const fileSize = new Blob([file.fileContents]).size;
  const fileSizeKB = (fileSize / 1024).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Properties</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="size-4" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <FileIcon className="size-8 text-blue-500" />
            <div>
              <h4 className="font-medium">{file.filePath.split('/').pop()}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">{file.language} file</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Path:</span>
              <span className="text-sm font-mono">{file.filePath}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Size:</span>
              <span className="text-sm">{fileSizeKB} KB ({fileSize} bytes)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Language:</span>
              <span className="text-sm">{file.language}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Lines:</span>
              <span className="text-sm">{file.fileContents.split('\n').length}</span>
            </div>
          </div>
        </div>
        
        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
